import { Link } from "react-router-dom";
import { Tent, Award, ShieldCheck, BarChart3, Users, Upload, ArrowRight, MapPin, ClipboardList, Database, Globe, ExternalLink } from "lucide-react";
import { motion } from "framer-motion";
import { PublicStats } from "@/components/PublicStats";
import { ChairmanQuote } from "@/components/ChairmanQuote";
import { HeroCounters } from "@/components/HeroCounters";
import { ResourcesSection } from "@/components/ResourcesSection";
import { GolonganPie } from "@/components/GolonganPie";
import { PromoSection } from "@/components/PromoSection";

const HERO = "/hero-pramuka.webp";

export default function Landing() {
  const features = [
    { icon: Users, title: "Pendataan Anggota", desc: "Setiap pramuka mengisi data diri secara mandiri: NTA, golongan, gudep, kwarran, dan tanggal pelantikan." },
    { icon: Award, title: "Catatan Prestasi", desc: "Unggah prestasi, TKK/TKU beserta sertifikat pendukung dari tingkat Ranting hingga Internasional." },
    { icon: ShieldCheck, title: "Verifikasi Berjenjang", desc: "Pembina Gudep memvalidasi awal, Admin Kwarcab memverifikasi dan merekap data resmi." },
    { icon: BarChart3, title: "Dashboard & Ekspor", desc: "Statistik anggota per kwarran & golongan, tren prestasi, dan ekspor data ke Excel/CSV." },
  ];

  return (
    <div className="min-h-screen grain">
      <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-xl bg-[#4A2E2B] flex items-center justify-center">
            <Tent className="h-5 w-5 text-[#E5A93C]" />
          </div>
          <div className="leading-tight">
            <div className="font-display font-extrabold text-[#1F1917] text-sm">SIPRAM DEPOK</div>
            <div className="text-[10px] uppercase tracking-widest text-[#8C827A] font-bold">Kwarcab Kota Depok</div>
          </div>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <a href="https://pramukakotadepok.or.id/" target="_blank" rel="noreferrer" data-testid="landing-site-utama-link"
            className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-[#4A2E2B] px-3.5 py-2 rounded-full border border-[#E5DFD7] bg-white hover:bg-[#F7F4EF] transition-colors">
            <Globe className="h-4 w-4 text-[#B85C38]" /> pramukakotadepok.or.id <ExternalLink className="h-3 w-3 text-[#8C827A]" />
          </a>
          <Link to="/auth" data-testid="landing-masuk-btn"
            className="inline-flex items-center gap-2 bg-[#4A2E2B] text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-[#3a2320] transition-colors">
            Masuk / Daftar <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-16 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="inline-flex items-center gap-2 bg-[#E5A93C]/15 text-[#8a5a10] px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide mb-6">
            <MapPin className="h-3.5 w-3.5" /> Satu Data Pramuka Kota Depok
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#1F1917] leading-[1.05]">
            Pusat data <span className="text-[#B85C38]">Pramuka</span> Kota Depok
          </h1>
          <p className="mt-6 text-base sm:text-lg text-[#4A423F] leading-relaxed max-w-xl">
            Pusat data Pramuka Kota Depok yang dikelola oleh Pusat Data dan Informasi (Pusdatin) Kwarcab Pramuka Kota Depok untuk Pramuka Depok Maju
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/auth" data-testid="landing-daftar-btn"
              className="inline-flex items-center gap-2 bg-[#E5A93C] text-[#2A1F1B] px-6 py-3.5 rounded-full font-bold hover:bg-[#d69a2b] transition-colors">
              Daftar Sebagai Pramuka <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/auth"
              className="inline-flex items-center gap-2 bg-white border border-[#E5DFD7] text-[#4A2E2B] px-6 py-3.5 rounded-full font-bold hover:bg-[#F7F4EF] transition-colors">
              Login Pembina / Admin
            </Link>
            <Link to="/petunjuk" data-testid="landing-hero-guide-btn"
              className="inline-flex items-center gap-2 bg-white border border-[#E5DFD7] text-[#4A2E2B] px-6 py-3.5 rounded-full font-bold hover:bg-[#F7F4EF] transition-colors">
              <ClipboardList className="h-4 w-4" /> Petunjuk Pengisian
            </Link>
          </div>
          <HeroCounters />
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }}
          className="relative">
          <GolonganPie />
          <div className="relative">
          <div className="absolute -inset-4 bg-[#E5A93C]/20 rounded-[2rem] rotate-3" />
          <img src={HERO} alt="Pramuka Siaga Kota Depok" className="relative rounded-[1.75rem] shadow-xl w-full h-[340px] object-cover object-[35%_center]" />
          <div className="absolute -top-4 right-4 bg-white rounded-xl shadow-md border border-[#E5DFD7] px-3 py-2 flex items-center gap-2 z-10">
            <img src="/bg/belimbing.svg" alt="" className="h-6 w-6" />
            <div className="leading-tight">
              <div className="text-[10px] text-[#8C827A] font-semibold">Pramuka Depok</div>
              <div className="font-display font-bold text-[#1F1917] text-xs">Kuat Pramukanya, Hebat Pembinanya</div>
            </div>
          </div>
          <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-md border border-[#E5DFD7] px-3 py-2 flex items-center gap-2">
            <div className="h-7 w-7 rounded-lg bg-emerald-100 flex items-center justify-center">
              <Award className="h-4 w-4 text-emerald-700" />
            </div>
            <div className="leading-tight">
              <div className="text-[10px] text-[#8C827A] font-semibold">Panca Waluya</div>
              <div className="font-display font-bold text-[#1F1917] text-xs">Cageur, Bageur, Bener, Pinter, Singer</div>
            </div>
          </div>
          </div>
        </motion.div>
      </section>

      <ChairmanQuote />

      <ResourcesSection />

      <PublicStats />

      <PromoSection />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div key={f.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}
                className="bg-white rounded-xl border border-[#E5DFD7] shadow-sm p-6 hover:shadow-md hover:-translate-y-1 transition-all">
                <div className="h-11 w-11 rounded-xl bg-[#4A2E2B] flex items-center justify-center mb-4">
                  <Icon className="h-5 w-5 text-[#E5A93C]" />
                </div>
                <h3 className="font-display text-lg font-semibold text-[#2A1F1B] mb-1.5">{f.title}</h3>
                <p className="text-sm text-[#4A423F] leading-relaxed">{f.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      <footer className="border-t border-[#E5DFD7] py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-[#8C827A]">
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
            <span>© 2026 Kwartir Cabang Gerakan Pramuka Kota Depok · SIPRAM DEPOK</span>
            <a href="https://pramukakotadepok.or.id/" target="_blank" rel="noreferrer" data-testid="footer-site-utama-link"
              className="inline-flex items-center gap-1.5 font-semibold text-[#B85C38] hover:underline">
              <Globe className="h-3.5 w-3.5" /> Situs Resmi: pramukakotadepok.or.id
            </a>
          </div>
          <div data-testid="footer-pusdatin" className="inline-flex items-center gap-2.5 bg-white border border-[#E5DFD7] rounded-full pl-1.5 pr-4 py-1.5">
            <div className="h-7 w-7 rounded-full bg-[#4A2E2B] flex items-center justify-center">
              <Database className="h-3.5 w-3.5 text-[#E5A93C]" />
            </div>
            <span>Dibuat dan Dikelola oleh <span className="font-bold text-[#4A2E2B]">PUSDATIN Pramuka Kota Depok</span> · <a href="https://wa.me/6289650041023" target="_blank" rel="noreferrer" data-testid="footer-pusdatin-wa" className="font-semibold text-[#B85C38] hover:underline">0896-5004-1023</a></span>
          </div>
        </div>
      </footer>
    </div>
  );
}
